import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getContents } from '../api/contentApi';
import { getTopicAreas } from '../api/topicAreaApi';
import { Content, TopicArea } from '../types';
import { getFileTypeInfo } from '../utils/fileUtils';
import { useAuth } from '../hooks/useAuth';
import './ArchiveListPage.css';

type ArchiveViewMode = 'table' | 'gallery';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getArchiveThumbnail = (content: Content): { src?: string; icon?: string; label: string } => {
  const primaryAttachment = content.attachments?.[0];
  const isImage = primaryAttachment?.file_type === '이미지' ||
    primaryAttachment?.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  if (primaryAttachment && isImage) {
    return {
      src: `${API_BASE_URL}${primaryAttachment.thumb_small || primaryAttachment.thumb_middle || primaryAttachment.file_url}`,
      label: primaryAttachment.file_name || content.title
    };
  }

  if (primaryAttachment) {
    const isLabel = !primaryAttachment.file_type?.includes('/') &&
      !primaryAttachment.file_type?.includes('application');
    const labelIconMap: Record<string, string> = {
      'Word 문서': 'DOC',
      'Excel 문서': 'XLS',
      'PowerPoint 문서': 'PPT',
      'PDF 문서': 'PDF',
      '이미지': 'IMG',
      '비디오': 'VID',
      '오디오': 'AUD',
      '압축 파일': 'ZIP',
      '텍스트': 'TXT'
    };
    const fileInfo = isLabel
      ? { icon: labelIconMap[primaryAttachment.file_type] || 'FILE' }
      : getFileTypeInfo(primaryAttachment.file_name, primaryAttachment.file_type);

    return {
      icon: fileInfo.icon,
      label: primaryAttachment.file_name || content.title
    };
  }

  if (content.thumbnail) {
    return { src: content.thumbnail, label: content.title };
  }

  return { icon: 'ARC', label: content.title };
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const ArchiveListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoggedIn } = useAuth();
  const [contents, setContents] = useState<Content[]>([]);
  const [topicAreas, setTopicAreas] = useState<TopicArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ArchiveViewMode>('table');
  const [searchText, setSearchText] = useState('');

  const selectedTopicAreaId = searchParams.get('topicAreaId');
  const selectedTopicAreaNumber = selectedTopicAreaId ? Number(selectedTopicAreaId) : null;

  useEffect(() => {
    const fetchArchives = async () => {
      try {
        setLoading(true);
        setError('');
        const [contentData, topicAreaData] = await Promise.all([
          getContents(undefined, null, selectedTopicAreaNumber),
          getTopicAreas()
        ]);

        setContents(contentData);
        setTopicAreas(topicAreaData);
      } catch (err) {
        console.error('기록 아카이브 조회 실패:', err);
        setError('기록 아카이브를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchArchives();
  }, [selectedTopicAreaNumber]);

  const selectedTopicArea = useMemo(
    () => topicAreas.find(area => area.id === selectedTopicAreaNumber) || null,
    [selectedTopicAreaNumber, topicAreas]
  );

  const filteredContents = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return contents;

    return contents.filter(content => {
      const topicTitle = content.topicArea?.title || '';
      const tags = (content.tags || []).map(tag => tag.name).join(' ');
      return [
        content.title,
        content.description,
        topicTitle,
        tags
      ].join(' ').toLowerCase().includes(query);
    });
  }, [contents, searchText]);

  const handleTopicAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ topicAreaId: value });
  };

  const createPath = selectedTopicAreaNumber
    ? `/content/create?topicAreaId=${selectedTopicAreaNumber}`
    : '/content/create';

  if (!isLoggedIn()) {
    return (
      <div className="archive-page">
        <section className="archive-empty-state">
          <h2>접근 권한이 없습니다</h2>
          <p>기록 아카이브는 로그인 후 조회할 수 있습니다.</p>
          <Link to="/login" className="modern-btn modern-btn-primary">로그인</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="archive-page">
      <header className="archive-header">
        <div>
          <p className="archive-eyebrow">Archive</p>
          <h1>기록 아카이브</h1>
          <p>
            {selectedTopicArea
              ? `${selectedTopicArea.title} 주제영역에 등록된 기록입니다.`
              : '주제영역에 등록된 기록을 목록으로 조회합니다.'}
          </p>
        </div>
        {user && (
          <Link to={createPath} className="modern-btn modern-btn-primary archive-create-button">
            기록 등록
          </Link>
        )}
      </header>

      <section className="archive-toolbar">
        <div className="archive-filter-group">
          <label>
            <span>주제영역</span>
            <select value={selectedTopicAreaId || ''} onChange={handleTopicAreaChange} className="modern-select">
              <option value="">전체 주제영역</option>
              {topicAreas.map(area => (
                <option key={area.id} value={area.id}>{area.title}</option>
              ))}
            </select>
          </label>
          <label>
            <span>검색</span>
            <input
              type="search"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder="제목, 설명, 태그 검색"
              className="modern-input"
            />
          </label>
        </div>

        <div className="archive-toolbar-actions">
          <span className="archive-count">{filteredContents.length}건</span>
          <div className="archive-view-toggle" aria-label="표시 방식">
            <button
              type="button"
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              title="목록형"
              aria-label="목록형"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            <button
              type="button"
              className={viewMode === 'gallery' ? 'active' : ''}
              onClick={() => setViewMode('gallery')}
              title="썸네일 갤러리형"
              aria-label="썸네일 갤러리형"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {error && <div className="archive-alert">{error}</div>}

      {loading ? (
        <div className="archive-loading">Loading...</div>
      ) : filteredContents.length === 0 ? (
        <section className="archive-empty-state">
          <h2>등록된 기록이 없습니다</h2>
          <p>선택한 조건에 맞는 기록이 없습니다.</p>
          <Link to={createPath} className="modern-btn modern-btn-primary">기록 등록</Link>
        </section>
      ) : viewMode === 'table' ? (
        <div className="archive-table-wrap">
          <table className="archive-table">
            <thead>
              <tr>
                <th>일자</th>
                <th>기록</th>
                <th>주제영역</th>
                <th>태그</th>
                <th>첨부</th>
                <th>등록일</th>
              </tr>
            </thead>
            <tbody>
              {filteredContents.map(content => (
                <tr key={content.id} onClick={() => navigate(`/content/${content.id}`)}>
                  <td className="archive-date-cell">{formatDate(content.timestamp)}</td>
                  <td>
                    <Link to={`/content/${content.id}`} className="archive-title-link" onClick={event => event.stopPropagation()}>
                      {content.title}
                    </Link>
                    <p className="archive-description">{content.description}</p>
                  </td>
                  <td>
                    {content.topicArea ? (
                      <Link
                        to={`/topic-areas/${content.topicArea.id}`}
                        className="archive-topic-link"
                        onClick={event => event.stopPropagation()}
                      >
                        {content.topicArea.title}
                      </Link>
                    ) : (
                      <span className="archive-muted">-</span>
                    )}
                  </td>
                  <td>
                    <div className="archive-tag-list">
                      {(content.tags || []).slice(0, 3).map(tag => (
                        <span key={tag.id}>#{tag.name}</span>
                      ))}
                      {(content.tags || []).length > 3 && <span>+{(content.tags || []).length - 3}</span>}
                    </div>
                  </td>
                  <td className="archive-attach-cell">{content.attachments?.length || 0}</td>
                  <td className="archive-date-cell">{formatDate(content.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="archive-gallery">
          {filteredContents.map(content => {
            const thumbnail = getArchiveThumbnail(content);
            return (
              <article key={content.id} className="archive-gallery-card" onClick={() => navigate(`/content/${content.id}`)}>
                <div className="archive-thumb">
                  {thumbnail.src ? (
                    <img src={thumbnail.src} alt={thumbnail.label} />
                  ) : (
                    <span>{thumbnail.icon}</span>
                  )}
                </div>
                <div className="archive-gallery-body">
                  <span className="archive-gallery-date">{formatDate(content.timestamp)}</span>
                  <h2>{content.title}</h2>
                  <p>{content.description}</p>
                  {content.topicArea && (
                    <span className="archive-gallery-topic">{content.topicArea.title}</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArchiveListPage;
